import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {growthbookCacheAtom} from 'atoms/growthbook';
import type {HomeTopPrioritiesMetricOut} from 'client';
import {WorkstreamMetricVisibility} from 'client';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {ROUTE_PATHS} from 'constant';
import {formatCompactMetricValue, parseMetricValueString} from 'helpers/compactMetricValue';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {DashboardState} from 'views/home/dashboard/dashboardModel';
import {Tile} from 'views/home/dashboard/Tile';
import {TileHeaderLink} from 'views/home/dashboard/TileListFooter';
import {TileRowLink} from 'views/home/dashboard/TileRowLink';
import {useHomeTopPriorities} from 'views/home/dashboard/useHomeTopPriorities';
import type {MetricDesiredDirection} from 'views/scorecards/scorecardDirection';
import {scorecardWorkstreamPath} from 'views/scorecards/ScorecardMetricNameCell';
import {getWorkstreamMetricStatusOption} from 'views/workstreams/detail/WorkstreamMetricStatus';

import styles from './TopPrioritiesTile.module.css';

const SKELETON_ROW_COUNT = 5;

/** "2026Q4" -> "Q4". */
export const quarterLabelFromPeriod = (period?: string): string =>
  period?.match(/Q[1-4]$/)?.[0] ?? '';

const displayValue = (value?: string | null): string =>
  value == null || value.trim() === '' ? '-' : formatCompactMetricValue(value).displayValue;

const clampProgress = (value: number): number => Math.min(Math.max(value, 0), 1);

/**
 * Share of the goal reached, clamped to 0–1, in the metric's desired direction
 * (the same direction Scorecards uses). For lower-is-better metrics, being at or
 * under the goal is a full bar. An unknown direction falls back to the plain
 * current ÷ goal ratio. Null when either side isn't numeric.
 */
export const progressTowardGoal = (
  current?: string | null,
  goal?: string | null,
  direction?: MetricDesiredDirection | null
): number | null => {
  const currentValue = parseMetricValueString(current);
  const goalValue = parseMetricValueString(goal);
  if (currentValue == null || goalValue == null) {
    return null;
  }
  if (currentValue === goalValue) {
    return 1;
  }
  if (direction === 'lower') {
    if (currentValue <= goalValue) {
      return 1;
    }
    return currentValue > 0 ? clampProgress(goalValue / currentValue) : null;
  }
  return goalValue === 0 ? null : clampProgress(currentValue / goalValue);
};

const ProgressBar = ({progress, color}: {progress: number | null; color: string}) => (
  <div
    className={styles.track}
    role={progress == null ? undefined : 'progressbar'}
    aria-valuemin={progress == null ? undefined : 0}
    aria-valuemax={progress == null ? undefined : 100}
    aria-valuenow={progress == null ? undefined : Math.round(progress * 100)}
    aria-label={
      progress == null
        ? undefined
        : t('dashboard_top_priorities_progress_aria', {
            percent: Math.round(progress * 100),
          }).toString()
    }
  >
    {progress != null && (
      <div className={styles.fill} style={{width: `${progress * 100}%`, backgroundColor: color}} />
    )}
  </div>
);

const MetricRowContent = ({metric}: {metric: HomeTopPrioritiesMetricOut}) => {
  const isRedacted = metric.visibility === WorkstreamMetricVisibility.REDACTED;
  const status = getWorkstreamMetricStatusOption(metric.status);

  return (
    <>
      <span className={styles.statusIcon} title={t(status.labelKey).toString()}>
        <UIIcon src={status.iconSrc} width={12} height={12} color={status.iconColor} />
      </span>
      <div className={styles.text}>
        {metric.workstream_name && (
          <Text tagName="div" size="xsmall" color="subtle" className={styles.workstreamName}>
            {metric.workstream_name}
          </Text>
        )}
        <Text tagName="div" className={styles.metricName} title={metric.display_name}>
          {metric.display_name}
        </Text>
      </div>
      <div className={styles.values}>
        {isRedacted ? (
          <Text color="faint">-</Text>
        ) : (
          <>
            <Text isBold>{displayValue(metric.current)}</Text>
            <Text color="subtle"> / {displayValue(metric.current_quarter_goal)}</Text>
          </>
        )}
      </div>
      <ProgressBar
        progress={
          isRedacted
            ? null
            : progressTowardGoal(
                metric.current,
                metric.current_quarter_goal,
                metric.desired_direction
              )
        }
        color={status.iconColor}
      />
    </>
  );
};

const MetricRow = ({metric}: {metric: HomeTopPrioritiesMetricOut}) => {
  const shade = withShade({});
  const workstreamPath = scorecardWorkstreamPath(metric.workstream_id ?? undefined);
  const content = (
    <div className={`${styles.row} ${shade.className}`} style={shade.style}>
      <MetricRowContent metric={metric} />
    </div>
  );
  return workstreamPath ? (
    <TileRowLink to={workstreamPath} analyticsTarget="north_star_metric">
      {content}
    </TileRowLink>
  ) : (
    <div className={styles.row}>
      <MetricRowContent metric={metric} />
    </div>
  );
};

const SkeletonRows = () => (
  <>
    {Array.from({length: SKELETON_ROW_COUNT}, (_, index) => (
      <div key={index} className={styles.row}>
        <Skeleton.Avatar size="small" style={{width: 12, height: 12}} />
        <div>
          <Skeleton.Text size="small" width={80} />
          <Skeleton.Text width={220} />
        </div>
        <Skeleton.Text width={72} />
        <Skeleton.Box style={{height: 8, borderRadius: 4}} />
      </div>
    ))}
  </>
);

/** Live North Star metric for each Scorecards pillar against this quarter's goal. */
export const TopPrioritiesTile = ({state}: {state: DashboardState}) => {
  const {isTopPrioritiesTileEnabled} = useAtomValue(growthbookCacheAtom);
  const {data} = useHomeTopPriorities({enabled: isTopPrioritiesTileEnabled});
  const isLoading = Boolean(state.topPrioritiesLoading) || !data;
  const bodyRef = useSkeletonFadeIn(isLoading);
  const quarter = quarterLabelFromPeriod(data?.period);

  return (
    <Tile
      tileId="topPriorities"
      bodyRef={bodyRef}
      accessory={<TileHeaderLink to={ROUTE_PATHS.SCORECARDS} />}
    >
      <Box display="flex" flexDirection="column" height="100%" style={{overflow: 'hidden'}}>
        <div className={styles.columns}>
          <Text size="xsmall" color="subtle" className={styles.sectionLabel}>
            {t('dashboard_top_priorities_north_star_metrics')}
          </Text>
          <Text size="small" color="subtle" className={styles.values}>
            <Text size="small" isBold color="standard">
              {t('dashboard_top_priorities_current')}
            </Text>
            {quarter && ` / ${t('dashboard_top_priorities_quarter_goal', {quarter})}`}
          </Text>
          <span />
        </div>
        <div className={styles.list}>
          {isLoading ? (
            <SkeletonRows />
          ) : (
            data.metrics?.map((metric) => <MetricRow key={metric.metric_id} metric={metric} />)
          )}
        </div>
      </Box>
    </Tile>
  );
};
